import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { LanguageDescription } from '@codemirror/language';
import { languages } from '@codemirror/language-data';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import { editorTheme, appSyntaxHighlighting } from './editor-theme';

export interface TextEditorViewProps {
  /** Buffer content (controlled). External changes reconcile into the view. */
  content: string;
  /** Drives language selection via `@codemirror/language-data`. */
  filename: string;
  readOnly?: boolean;
  onChange?: (next: string) => void;
  /**
   * Fired on every doc change WITHOUT materializing the document string —
   * `doc.toString()` per keystroke on a large file is exactly the main-thread
   * cost the buffer model avoids. Hosts that need
   * the text pull it on demand through {@link TextEditorViewProps.getTextRef}
   * at sync points (debounce boundary, save, tab switch, close).
   */
  onDocChanged?: () => void;
  /**
   * Receives an on-demand text getter for the live document. Deliberately NOT
   * cleared on unmount: the parent's flush-pending-edits cleanup runs AFTER
   * this component's (React destroys effects child-first), and a destroyed
   * EditorView's immutable state stays readable — so a tab-close flush can
   * still capture the final text.
   */
  getTextRef?: MutableRefObject<(() => string) | null>;
  /** Cmd/Ctrl+S keymap binding. */
  onSave?: () => void;
  className?: string;
}

/**
 * The editing wiring the pane threads into viewers that host a source-side
 * editor (TextEditorView itself, MarkdownView's raw side, HtmlView's source
 * side). Pane-level chrome (banners, dirty dot) stays OUT of this — viewers
 * only receive what the nested editor needs plus the dirty flag that pins
 * their preview/source toggle to source.
 */
export interface ViewerSourceEditing {
  readOnly: boolean;
  /** Unsaved edits exist — pins preview/source toggles to source. */
  dirty: boolean;
  onDocChanged?: () => void;
  getTextRef?: MutableRefObject<(() => string) | null>;
  onSave?: () => void;
}

function readOnlyExtensions(readOnly: boolean) {
  return [EditorState.readOnly.of(readOnly), EditorView.editable.of(!readOnly)];
}

type CopyState = 'idle' | 'copied' | 'error';

/**
 * CodeMirror 6 text editor/viewer: one `EditorView` per mounted component,
 * controlled `content` reconciled via a full-document transaction only when it
 * differs from the view's doc (prevents echo loops), language resolved lazily
 * from `@codemirror/language-data` (no match ⇒ plain text). Replaces the old
 * Prism-based CodeView — there are no highlight size caps; Lezer parses
 * incrementally, so large files render without freezing the main thread.
 */
export function TextEditorView({
  content,
  filename,
  readOnly = false,
  onChange,
  onDocChanged,
  getTextRef,
  onSave,
  className,
}: TextEditorViewProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const languageCompartment = useRef(new Compartment());
  const readOnlyCompartment = useRef(new Compartment());

  // Callback + initial-value refs keep the mount-once extension set stable.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onDocChangedRef = useRef(onDocChanged);
  onDocChangedRef.current = onDocChanged;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const getTextRefRef = useRef(getTextRef);
  getTextRefRef.current = getTextRef;
  const initialRef = useRef({ content, readOnly });

  const [langError, setLangError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<CopyState>('idle');

  const clipboardAvailable = typeof navigator !== 'undefined' && !!navigator.clipboard;

  useEffect(() => {
    if (!hostRef.current) return;
    const view = new EditorView({
      parent: hostRef.current,
      state: EditorState.create({
        doc: initialRef.current.content,
        extensions: [
          lineNumbers(),
          history(),
          highlightSelectionMatches(),
          keymap.of([
            {
              key: 'Mod-s',
              preventDefault: true,
              run: () => {
                onSaveRef.current?.();
                return true;
              },
            },
            ...defaultKeymap,
            ...searchKeymap,
            ...historyKeymap,
          ]),
          editorTheme,
          appSyntaxHighlighting,
          readOnlyCompartment.current.of(readOnlyExtensions(initialRef.current.readOnly)),
          languageCompartment.current.of([]),
          EditorView.updateListener.of((update) => {
            if (!update.docChanged) return;
            // Cheap notification first (no string materialization);
            // the optional call short-circuits toString when no onChange is
            // wired, so editing hosts pay nothing per keystroke here.
            onDocChangedRef.current?.();
            onChangeRef.current?.(update.state.doc.toString());
          }),
        ],
      }),
    });
    viewRef.current = view;
    // On-demand text getter for the host's sync points. NOT cleared on
    // unmount (see the prop doc): the parent's flush cleanup runs after ours
    // and the destroyed view's state remains readable.
    const externalGetText = getTextRefRef.current;
    if (externalGetText) externalGetText.current = () => view.state.doc.toString();
    return () => {
      viewRef.current = null;
      view.destroy();
    };
  }, []);

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: readOnlyCompartment.current.reconfigure(readOnlyExtensions(readOnly)),
    });
  }, [readOnly]);

  // Reconcile external content changes (reload, tab data) into the view — but
  // only when they actually differ from the doc, so the host echoing our own
  // onChange value back doesn't loop.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (view.state.doc.toString() === content) return;
    view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: content } });
  }, [content]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    setLangError(null);
    const desc = LanguageDescription.matchFilename(languages, filename.split('/').pop() ?? filename);
    if (!desc) {
      // No registered language ⇒ plain text (correct for .log, .env, unknown).
      view.dispatch({ effects: languageCompartment.current.reconfigure([]) });
      return;
    }
    let cancelled = false;
    desc
      .load()
      .then((support) => {
        if (cancelled || !viewRef.current) return;
        viewRef.current.dispatch({ effects: languageCompartment.current.reconfigure(support) });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLangError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [filename]);

  const handleCopy = useCallback(() => {
    if (!clipboardAvailable) return;
    // Copy the live doc (not the prop) so unsaved edits are what's copied.
    const text = viewRef.current?.state.doc.toString() ?? content;
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopyState('copied');
        window.setTimeout(() => setCopyState('idle'), 1500);
      })
      .catch(() => {
        setCopyState('error');
      });
  }, [content, clipboardAvailable]);

  const copyLabel = copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy';

  return (
    <div className={cn('flex h-full min-h-0 flex-col rounded-md bg-muted/30 text-sm', className)}>
      <div className="flex items-center justify-between border-b px-3 py-1.5">
        <span className="truncate font-mono text-xs text-muted-foreground">
          {filename}
          {langError && (
            <span className="ml-2 text-destructive" role="alert">
              · syntax highlighting failed to load: {langError}
            </span>
          )}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          disabled={!clipboardAvailable}
          className="h-7 px-2 text-xs"
        >
          {copyLabel}
        </Button>
      </div>
      <div ref={hostRef} className="min-h-0 flex-1 overflow-hidden" data-testid="text-editor-view" />
    </div>
  );
}
